import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Grid,
  Alert, CircularProgress, Card, CardContent, Chip,
  Divider, FormControl, InputLabel, Select, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItem, ListItemText, IconButton
} from '@mui/material';
import {
  LocalOffer as OfferIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import promotionApi from './promotionApi';

const UserPromotions = ({ userId = 'user123', onPromoApplied }) => {
  // State for available promotions
  const [availablePromos, setAvailablePromos] = useState([]);
  const [loadingPromos, setLoadingPromos] = useState(false);
  
  // State for applying promo
  const [formData, setFormData] = useState({
    promo_code: '',
    service_type: 'medicine',
    order_price: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [discount, setDiscount] = useState(null);
  const [showPromoList, setShowPromoList] = useState(false);
  const [copiedCode, setCopiedCode] = useState('');

  const SERVICE_TYPES = [
    { value: 'medicine', label: 'Medicine' },
    { value: 'diagnostic', label: 'Diagnostic' }
  ];

  // Fetch available promotions
  useEffect(() => {
    if (formData.service_type) {
      fetchAvailablePromos();
    }
  }, [formData.service_type]);

  const fetchAvailablePromos = async () => {
    setLoadingPromos(true);
    try {
      const response = await promotionApi.listPromotions({
        service_type: formData.service_type,
        user_id: userId
      });
      
      // Filter only active promos
      const activePromos = response.promotions.filter(p => p.status === 'active');
      setAvailablePromos(activePromos);
    } catch (err) {
      console.error('Error fetching promos:', err);
    } finally {
      setLoadingPromos(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const copyPromoCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setFormData(prev => ({ ...prev, promo_code: code }));
    setTimeout(() => setCopiedCode(''), 2000);
  };

  const handleApply = async (e) => {
    e.preventDefault();
    
    if (!formData.promo_code.trim()) {
      setError('Please enter a promo code');
      return;
    }

    if (!formData.order_price || parseFloat(formData.order_price) <= 0) {
      setError('Please enter a valid order price');
      return;
    }

    setLoading(true);
    setError('');
    setDiscount(null);

    try {
      const response = await promotionApi.applyPromotion({
        promo_code: formData.promo_code.toUpperCase().trim(),
        service_type: formData.service_type,
        order_price: parseFloat(formData.order_price),
        user_id: userId
      });

      setDiscount(response);
      
      // Callback to parent component if provided
      if (onPromoApplied) {
        onPromoApplied(response);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to apply promo code');
      setDiscount(null);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = () => {
    setFormData(prev => ({ ...prev, promo_code: '' }));
    setDiscount(null);
    setError('');
  };

  const getDiscountText = (promo) => {
    if (promo.discount_amount > 0) {
      return `₹${promo.discount_amount} OFF`;
    } else if (promo.discount_perc > 0) {
      const text = `${promo.discount_perc}% OFF`;
      if (promo.max_discount > 0) {
        return `${text} (Max ₹${promo.max_discount})`;
      }
      return text;
    }
    return 'Discount Available';
  };

  return (
    <Box>
      {/* Main Promo Application Card */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Apply Promotion Code
        </Typography>

        <Box component="form" onSubmit={handleApply} sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            {/* Service Type */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Service Type</InputLabel>
                <Select
                  name="service_type"
                  value={formData.service_type}
                  onChange={handleChange}
                  label="Service Type"
                  disabled={discount !== null}
                >
                  {SERVICE_TYPES.map(service => (
                    <MenuItem key={service.value} value={service.value}>
                      {service.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Order Price */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                type="number"
                label="Order Amount (₹)"
                name="order_price"
                value={formData.order_price}
                onChange={handleChange}
                inputProps={{ min: 1, step: 0.01 }}
                disabled={discount !== null}
              />
            </Grid>

            {/* Promo Code Input */}
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                required
                label="Promo Code"
                name="promo_code"
                value={formData.promo_code}
                onChange={handleChange}
                placeholder="Enter promo code"
                disabled={discount !== null}
                inputProps={{ style: { textTransform: 'uppercase' } }}
              />
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12} md={4}>
              {discount ? (
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  onClick={handleRemove}
                  sx={{ height: '56px' }}
                >
                  Remove Code
                </Button>
              ) : (
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  sx={{ height: '56px' }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Apply Code'}
                </Button>
              )}
            </Grid>

            {/* Browse Promos Button */}
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<OfferIcon />}
                onClick={() => setShowPromoList(true)}
                disabled={discount !== null}
              >
                Browse Available Offers ({availablePromos.length})
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Success - Discount Applied */}
        {discount && (
          <Card sx={{ mt: 3, bgcolor: 'success.light', border: '2px solid', borderColor: 'success.main' }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <CheckIcon sx={{ color: 'success.dark', mr: 1, fontSize: 30 }} />
                <Box>
                  <Typography variant="h6" color="success.dark">
                    {discount.title || 'Promo Applied Successfully!'}
                  </Typography>
                  <Typography variant="body2" color="success.dark">
                    Code: {discount.promo_code}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Original Price:
                  </Typography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  <Typography variant="body1" sx={{ textDecoration: 'line-through' }}>
                    ₹{discount.original_price}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2" color="success.dark" fontWeight="bold">
                    Discount:
                  </Typography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  <Typography variant="body1" color="success.dark" fontWeight="bold">
                    - ₹{discount.discount_amount}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="h6">
                    Final Amount:
                  </Typography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    ₹{discount.final_price}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Alert severity="success" icon={<CheckIcon />}>
                    🎉 You saved ₹{discount.savings}!
                  </Alert>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}
      </Paper>

      {/* Available Promotions Dialog */}
      <Dialog 
        open={showPromoList} 
        onClose={() => setShowPromoList(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <OfferIcon sx={{ mr: 1 }} />
            Available Offers for {SERVICE_TYPES.find(s => s.value === formData.service_type)?.label}
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingPromos ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : availablePromos.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography color="text.secondary">
                No active promotions available
              </Typography>
            </Box>
          ) : (
            <List>
              {availablePromos.map((promo, index) => (
                <React.Fragment key={promo._id || index}>
                  <ListItem
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 2,
                      bgcolor: 'background.paper',
                      flexDirection: 'column',
                      alignItems: 'stretch'
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" width="100%" mb={1}>
                      <Box>
                        <Typography variant="h6" color="primary">
                          {promo.title || promo.promo_code}
                        </Typography>
                        <Chip 
                          label={getDiscountText(promo)}
                          color="success"
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                      <IconButton
                        color="primary"
                        onClick={() => copyPromoCode(promo.promo_code)}
                        sx={{ height: 40 }}
                      >
                        {copiedCode === promo.promo_code ? <CheckIcon /> : <CopyIcon />}
                      </IconButton>
                    </Box>

                    <Typography variant="body2" color="text.secondary" mb={1}>
                      {promo.description || 'Apply this code to get discount on your order'}
                    </Typography>

                    <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
                      <Chip 
                        label={`Code: ${promo.promo_code}`}
                        size="small"
                        variant="outlined"
                      />
                      {promo.min_order_value > 0 && (
                        <Chip 
                          label={`Min Order: ₹${promo.min_order_value}`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                      <Chip 
                        label={`Valid till: ${new Date(promo.end_date).toLocaleDateString()}`}
                        size="small"
                        variant="outlined"
                      />
                    </Box>

                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => {
                        copyPromoCode(promo.promo_code);
                        setShowPromoList(false);
                      }}
                      sx={{ mt: 1 }}
                    >
                      Use This Code
                    </Button>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPromoList(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserPromotions;